import { z } from "zod";

import { baseApiResponse, strKeyAnyValResolver } from "./BaseApiResponse";
import { Currencies } from "./Currency";

export enum GameCategories {
	POPULAR = "popular",
	NEW = "new",
	SLOTS = "slots",
	JACKPOTS = "jackpots",
	VIRTUALSPORTS = "virtualsports",
	poker = "poker",
	VIDEOPOKER = "videopoker",
	MINES = "mines",
	TABLEGAMES = "tablegames",
	OTHER = "other",
	LIVECASINO = "livecasino",
	BACCARAT = "baccarat",
	BLACKJACKS = "blackjacks",
	SCRATCHCARDS = "scratchcards",
	DEFTONES = "deftones",
	CAYETANO = "cayetano",
	ROULETTE = "roulette",
	MEGAWAYS = "megaways",
	CRASHGAME = "crashgame",
	EXTREMELYHIGHRTP = "extremelyhighrtp",
	FREEBONUS = "freebonus"
}

export const getListGamesRequestParamsResolver = z.object({
	lastGames: z.boolean().optional(),
	category: z.string().optional(),
	order_by: z.string().optional(),
	platform: z.string().optional(),
	launchCode: z.string().optional(),
	merchant: z.string().optional(),
	demo: z.boolean().optional(),
	slim: z.boolean().optional(),
	limit: z.number().optional(),
	returnUrl: z.string().optional()
});

export type GetListGamesRequestParamsModel = z.infer<typeof getListGamesRequestParamsResolver>;

const gameCategorySchema = z.object({
	ID: z.string(),
	Trans: strKeyAnyValResolver,
	Tags: z.array(z.string()),
	Name: strKeyAnyValResolver,
	CSort: z.string(),
	CSubSort: z.string(),
	Slug: z.string(),
	CustomSort: z
		.array(
			z.object({
				Lang: strKeyAnyValResolver
			})
		)
		.or(
			z.object({
				Lang: strKeyAnyValResolver
			})
		),
	menuId: z.string()
});
export type GameCategoryModel = z.infer<typeof gameCategorySchema>;

const gameMerchantSchema = z.object({
	ID: z.string(),
	Name: z.string(),
	IDParent: z.union([z.string(), z.null()]),
	Alias: z.string(),
	Image: z.string(),
	Weight: z.string(),
	menuId: z.string()
});

export type GameMerchantModel = z.infer<typeof gameMerchantSchema>;

const gameCountryRestrictionSchema = z.object({
	Countries: z.array(z.string()),
	ID: z.string(),
	IDMerchant: z.string(),
	IsDefault: z.string(),
	Name: z.string(),
	IDParent: z.string(),
	IDApiTemplate: z.string(),
	Subdivisions: z.array(z.any())
});

export type GameCountryRestrictionModel = z.infer<typeof gameCountryRestrictionSchema>;

const gameMerchantCurrencySchema = z.object({
	ID: z.string(),
	Name: z.string(),
	IDMerchant: z.string(),
	Currencies: z.array(z.nativeEnum(Currencies)),
	DefaultCurrency: z.string(),
	IsDefault: z.string()
});

export type GameMerchantCurrencyModel = z.infer<typeof gameMerchantCurrencySchema>;

const gameResolver = z.object({
	ID: z.string(),
	Image: z.string(),
	Url: z.string(),
	Name: strKeyAnyValResolver.or(strKeyAnyValResolver.array()),
	Description: strKeyAnyValResolver.or(strKeyAnyValResolver.array()),
	MobileUrl: z.string(),
	Branded: z.number(),
	SuperBranded: z.number().optional(),
	hasDemo: z.number(),
	CategoryID: z.array(z.string()),
	SortPerCategory: z.record(z.string(), z.number()).or(z.array(z.record(z.string(), z.number()))),
	MerchantID: z.string(),
	SubMerchantID: z.string().nullable(),
	AR: z.string(),
	IDCountryRestriction: z.string(),
	Sort: z.string(),
	PageCode: z.string().nullable(),
	MobilePageCode: z.string().nullable(),
	MobileAndroidPageCode: z.string().nullable(),
	MobileWindowsPageCode: z.string().nullable(),
	ExternalCode: z.string().nullable(),
	MobileExternalCode: z.string().nullable(),
	ImageFullPath: z.string(),
	WorkingHours: z.string().nullable(),
	IsVirtual: z.string(),
	TableID: z.string(),
	CustomSort: strKeyAnyValResolver.or(z.array(strKeyAnyValResolver)), // Empty object
	RTP: z.string().nullable(),
	BrandedNew: z.number().optional(),
	MinBetDefault: z.string().nullable(),
	MaxBetDefault: z.string().nullable(),
	MaxMultiplier: z.string().nullable(),
	IDMerchantsCurrencies: z.string(),
	Freeround: z.string().optional(),
	BonusBuy: z.number(),
	Megaways: z.number(),
	Freespins: z.number(),
	LaunchCode: z.string()
});

export type GameModel = z.infer<typeof gameResolver>;

export const gameListResponseResolver = baseApiResponse.extend({
	data: z.object({
		categories: z.array(gameCategorySchema),
		games: z.array(gameResolver),
		merchants: z.record(z.string(), gameMerchantSchema),
		countriesRestrictions: z.record(z.string(), gameCountryRestrictionSchema),
		merchantsCurrencies: z.array(gameMerchantCurrencySchema)
	})
});

export type GameListResponseModel = z.infer<typeof gameListResponseResolver>;

export const launchGameRequestParamsResolver = z.object({
	launchCode: z.string(),
	merchantId: z.string(),
	wallet: z.number().optional(),
	currency: z.nativeEnum(Currencies).optional(),
	demo: z.boolean()
});

export type LaunchGameRequestParamsModel = z.infer<typeof launchGameRequestParamsResolver>;

export const launchGameResponseResolver = baseApiResponse.extend({
	data: z.object({
		merchantId: z.string(),
		merchant: z.string().optional(),
		mobilePlatform: z.boolean(),
		gameHtml: z.string(),
		gameScript: z.string(),
		config: z.object({
			AR: z.string(),
			AuthToken: z.string()
		}),
		isRestricted: z.boolean().optional()
	})
});

export type LaunchGameResponseModel = z.infer<typeof launchGameResponseResolver>;

export const favoriteGameResponseResolver = baseApiResponse.extend({
	data: z.object({
		favorite: z.number(),
		game_id: z.number().or(z.string())
	})
});

export type FavoriteGameResponseModel = z.infer<typeof favoriteGameResponseResolver>;

export const listFavoriteGameResponseResolver = baseApiResponse.extend({
	data: z
		.object({
			game_id: z.number().or(z.string())
		})
		.array()
});

export type ListFavoriteGameResponseModel = z.infer<typeof listFavoriteGameResponseResolver>;
